import { buildSummary } from './boardDelivery'
import type { ReactNode } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { releaseApi, type WorkItemSummary } from '../api'

const deliveryQuery = (item: WorkItemSummary) => ({
    queryKey: ['board-delivery', item.id, item.workItemType],
    queryFn: () => releaseApi.getDeliveryStatus(item.id, item.workItemType),
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 1,
})

const mergeLabels: Record<string, string> = {
    'no-pr': 'No PR', open: 'PR open', merged: 'Merged', abandoned: 'PR abandoned',
    conflicts: 'Merge conflicts', blocked: 'Merge blocked', unknown: 'Unknown',
}

function statusColour(label: string): string {
    if (['Merged', 'Succeeded'].includes(label)) { return 'text-emerald-400' }
    if (['Failed', 'Merge conflicts', 'Merge blocked'].includes(label)) { return 'text-red-400' }
    if (['Running', 'Queued', 'PR open'].includes(label)) { return 'text-blue-300' }
    return 'text-slate-400'
}

export function BoardDeliveryStatus({ item }: { item: WorkItemSummary }) {
    const query = useQuery(deliveryQuery(item))
    if (query.isError) {
        return <button className="text-[10px] text-amber-400 text-left" onClick={() => { void query.refetch() }}>Status unavailable · Retry</button>
    }
    if (!query.data) { return <span className="text-[10px] text-slate-500">Loading merge and build…</span> }
    const status = query.data
    const merge = mergeLabels[status.mergeState] ?? 'Unknown'
    const build = buildSummary(status)
    const buildLabel = status.mergeState === 'merged' ? 'Build' : 'PR checks'
    return (
        <div className="space-y-1 border-t border-slate-700/50 pt-1.5 text-[10px]" title={`Checked ${new Date(status.fetchedAt).toLocaleTimeString()}`}>
            <div className={statusColour(merge)}>
                {status.prUrl ? <a href={status.prUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">{merge} ↗</a> : merge}
                {status.targetBranch && <span className="text-slate-500 break-all"> → {status.targetBranch}</span>}
            </div>
            <div className={statusColour(build)}>{buildLabel}: {build}</div>
            {status.builds.map(b => (
                <a key={b.id} href={b.url ?? undefined} target="_blank" rel="noopener noreferrer" className="block text-slate-400 hover:text-slate-200 hover:underline break-words">
                    {b.name} #{b.id} · {b.result ?? b.state} ↗
                </a>
            ))}
        </div>
    )
}

export function BoardEpicGroup({ epicId, title, items, children }: { epicId: number | null; title: string; items: WorkItemSummary[]; children: ReactNode }) {
    const queries = useQueries({ queries: items.map(deliveryQuery) })
    const statuses = queries.flatMap(q => !q.isError && q.data ? [q.data] : [])
    const unavailable = queries.filter(q => q.isError).length
    const pending = queries.filter(q => !q.isError && !q.data).length
    const merged = statuses.filter(s => s.mergeState === 'merged').length
    const succeeded = statuses.filter(s => buildSummary(s) === 'Succeeded').length
    const failed = statuses.filter(s => buildSummary(s) === 'Failed').length
    const blocked = statuses.filter(s => ['conflicts', 'blocked'].includes(s.mergeState)).length
    return (
        <section className="space-y-1.5" aria-label={title}>
            {epicId !== null && <div className="rounded-lg bg-purple-950/20 border border-purple-500/20 p-2 space-y-1">
                <div className="text-[10px] text-purple-300 font-semibold">Epic #{epicId} · {title}</div>
                <div className="text-[10px] text-slate-400">{merged}/{items.length} merged · {succeeded}/{items.length} checks/builds passed</div>
                <div className="text-[9px] text-slate-500">Items in this column · refreshes every minute</div>
                {failed > 0 && <div className="text-[10px] text-red-400">{failed} failed checks/builds</div>}
                {blocked > 0 && <div className="text-[10px] text-red-400">{blocked} blocked merges</div>}
                {pending > 0 && <div className="text-[10px] text-slate-500">{pending} loading</div>}
                {unavailable > 0 && <div className="text-[10px] text-amber-400">{unavailable} status unavailable</div>}
            </div>}
            {children}
        </section>
    )
}
